/**
 * Quick Start Modal Component
 * Step-by-step guide for new users to get started with ZO Bull
 * Version: 6.2.0
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './QuickStartModal.css'

interface QuickStartStep {
  id: string
  title: string
  description: string
  icon: string
  action?: () => void
  path?: string
  status: 'pending' | 'completed' | 'current'
}

interface QuickStartModalProps {
  isOpen: boolean
  onClose: () => void
}

function QuickStartModal({ isOpen, onClose }: QuickStartModalProps) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<string[]>([])

  // Load completed steps from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('quickstart_completed')
    if (saved) {
      setCompletedSteps(JSON.parse(saved))
    }
  }, [isOpen])

  // Save completed steps to localStorage
  const markStepComplete = (stepId: string) => {
    if (!completedSteps.includes(stepId)) {
      const updated = [...completedSteps, stepId]
      setCompletedSteps(updated)
      localStorage.setItem('quickstart_completed', JSON.stringify(updated))
    }
  }

  const steps: QuickStartStep[] = [
    {
      id: 'connect-broker',
      title: 'Connect Your Broker',
      description: 'Link your trading account to start automated trading. We support 82+ brokers including Zerodha, Angel One, and crypto exchanges.',
      icon: '🔗',
      path: '/brokers',
      status: completedSteps.includes('connect-broker') ? 'completed' : currentStep === 0 ? 'current' : 'pending'
    },
    {
      id: 'create-strategy',
      title: 'Create a Strategy',
      description: 'Build your own strategy or choose from pre-built templates. Use our AI Strategy Builder to create strategies with natural language.',
      icon: '📊',
      path: '/strategies',
      status: completedSteps.includes('create-strategy') ? 'completed' : currentStep === 1 ? 'current' : 'pending'
    },
    {
      id: 'backtest',
      title: 'Run a Backtest',
      description: 'Test your strategy on 5+ years of historical data to validate its performance before risking real money.',
      icon: '🔬',
      path: '/strategies?tab=backtest',
      status: completedSteps.includes('backtest') ? 'completed' : currentStep === 2 ? 'current' : 'pending'
    },
    {
      id: 'paper-trade',
      title: 'Practice with Paper Trading',
      description: 'Practice trading with ₹10,00,000 virtual cash. Trade with real market prices but zero financial risk.',
      icon: '📝',
      path: '/strategies?tab=paper',
      status: completedSteps.includes('paper-trade') ? 'completed' : currentStep === 3 ? 'current' : 'pending'
    },
    {
      id: 'go-live',
      title: 'Go Live',
      description: 'Ready to trade with real money? Start with small positions and monitor your trades closely.',
      icon: '🚀',
      status: completedSteps.includes('go-live') ? 'completed' : currentStep === 4 ? 'current' : 'pending'
    }
  ]

  const totalSteps = steps.length
  const progress = ((currentStep + 1) / totalSteps) * 100
  const overallProgress = (completedSteps.length / totalSteps) * 100

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = () => {
    markStepComplete(steps[currentStep].id)
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleComplete = () => {
    markStepComplete(steps[currentStep].id)
    localStorage.setItem('quickstart_shown', 'true')
    onClose()
  }

  const handleAction = (step: QuickStartStep) => {
    markStepComplete(step.id)
    if (step.path) {
      navigate(step.path)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="quickstart-overlay" onClick={onClose}>
      <div className="quickstart-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="quickstart-header">
          <div className="quickstart-title">
            <span className="quickstart-icon">🚀</span>
            <div>
              <h2>Quick Start Guide</h2>
              <p>Get started in 5 easy steps</p>
            </div>
          </div>
          <button className="quickstart-close" onClick={onClose}>✕</button>
        </div>

        {/* Progress Bar */}
        <div className="quickstart-progress">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="progress-text">Step {currentStep + 1} of {totalSteps}</span>
        </div>

        {/* Overall Progress */}
        <div className="overall-progress">
          <span className="progress-label">Overall Progress</span>
          <div className="overall-track">
            <div className="overall-fill" style={{ width: `${overallProgress}%` }} />
          </div>
          <span className="progress-count">{completedSteps.length}/{totalSteps} completed</span>
        </div>

        {/* Step Indicators */}
        <div className="step-indicators">
          {steps.map((step, index) => (
            <button
              key={step.id}
              className={`step-indicator ${index === currentStep ? 'active' : ''} ${completedSteps.includes(step.id) ? 'completed' : ''}`}
              onClick={() => setCurrentStep(index)}
            >
              <span className="step-number">
                {completedSteps.includes(step.id) ? '✓' : index + 1}
              </span>
              <span className="step-icon">{step.icon}</span>
            </button>
          ))}
        </div>

        {/* Step Content */}
        <div className="quickstart-content">
          <div className="step-icon-large">{steps[currentStep].icon}</div>
          <h3>{steps[currentStep].title}</h3>
          <p>{steps[currentStep].description}</p>
          
          {/* Step Preview */}
          <div className="step-preview">
            <div className="preview-card">
              <span className="preview-icon">💡</span>
              <span className="preview-tip">
                {currentStep === 0 && "Tip: Start with Zerodha or Angel One for Indian markets"}
                {currentStep === 1 && "Tip: Try the RSI Reversal strategy for beginners"}
                {currentStep === 2 && "Tip: Run backtest on at least 1 year of data"}
                {currentStep === 3 && "Tip: Paper trade for 2 weeks before going live"}
                {currentStep === 4 && "Tip: Start with 10% of your capital"}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="quickstart-actions">
          {currentStep > 0 && (
            <button className="btn btn-secondary" onClick={handlePrevious}>
              ← Previous
            </button>
          )}
          <button className="btn btn-ghost" onClick={handleSkip}>
            Skip Step
          </button>
          {steps[currentStep].path ? (
            <button className="btn btn-primary" onClick={() => handleAction(steps[currentStep])}>
              {completedSteps.includes(steps[currentStep].id) ? 'Go Again' : 'Go Now'} →
            </button>
          ) : currentStep === totalSteps - 1 ? (
            <button className="btn btn-success" onClick={handleComplete}>
              Complete Setup ✓
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleNext}>
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuickStartModal